import { QueryClientProvider } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, useEffect, useState } from 'react';

import { useSession } from '@/entities/session';
import {
  createApiClient,
  createQueryClient,
  restoreSession,
  SessionRefreshProvider,
  TRPCProvider,
} from '@/shared/api';

/**
 * 앱 전역 배선.
 *
 * QueryClient 와 tRPC 클라이언트는 렌더마다 새로 만들면 캐시도 세션도 날아가므로 한 번만 만든다.
 * 서버 세션과 화면 스토어를 잇는 지점도 여기다 — 스토어는 서버를 모르고, 전송 계층은 화면을 모른다.
 */
export function AppProviders({ children }: { children: ReactNode }): ReactElement {
  const [queryClient] = useState(createQueryClient);
  const [api] = useState(() =>
    createApiClient({
      // 재발급까지 실패했다면 서버 세션은 이미 끝났다. 화면도 그 자리에서 비운다 —
      //   남겨 두면 다음 요청이 401 로 튕길 때까지 로그인된 척한다.
      onSessionLost: () => {
        useSession.getState().signOut();
      },
    }),
  );

  // 액세스 토큰은 메모리에만 있어 새로고침이면 사라진다 — 쿠키에 남은 리프레시 토큰으로
  //   세션을 한 번 되살린다. StrictMode 가 이 효과를 두 번 실행해도 재발급기가 합류시킨다.
  useEffect(() => {
    void restoreSession(api.refreshSession).then((member) => {
      if (member !== null) {
        useSession.getState().signIn(member);
      }
    });
  }, [api]);

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={api.client} queryClient={queryClient}>
        <SessionRefreshProvider refresh={api.refreshSession}>{children}</SessionRefreshProvider>
      </TRPCProvider>
    </QueryClientProvider>
  );
}
